import { ref, watch, onMounted, computed } from 'vue';
import { useApi } from '@/shared/composables/useApi';
import { useAuth } from '@/shared/composables/useAuth';
import { useNotifications } from '@/shared/composables/useNotifications';

interface User {
  user_id: string;
  email: string;
  name?: string;
  roles: string[];
}

export function useUserManagement() {
  const { getToken } = useAuth();
  const api = useApi();
  const { notify } = useNotifications();
  const users = ref<User[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const availableRoles = ref<string[]>([]);
  const initialized = ref(false);
  const updatingUsers = ref<{ [key: string]: boolean }>({});
  
  // Search and filter state
  const searchQuery = ref('');
  const selectedRoles = ref<string[]>([]);
  
  // Filtered users
  const filteredUsers = computed(() => {
    return users.value.filter(user => {
      const matchesSearch = searchQuery.value === '' || 
        user.email.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        (user.name?.toLowerCase() || '').includes(searchQuery.value.toLowerCase());
        
      const matchesRoles = selectedRoles.value.length === 0 || 
        selectedRoles.value.every(role => user.roles.includes(role));
        
      return matchesSearch && matchesRoles;
    });
  });

  const fetchAvailableRoles = async () => {
    try {
      const token = await getToken();
      const response = await api.get('/users/roles', {
        headers: { Authorization: `Bearer ${token}` }
      });
      availableRoles.value = response;
    } catch (err) {
      console.error('Error fetching available roles:', err);
      // Fallback to basic roles if API fails
      availableRoles.value = ['user', 'admin'];
    }
  };

  const fetchUsers = async () => {
    if (loading.value) return;
    
    loading.value = true;
    error.value = null;
    try {
      const token = await getToken();
      const response = await api.get('/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ensure each user has a roles array
      users.value = response.map((user: any) => ({
        ...user,
        roles: Array.isArray(user.roles) ? user.roles : []
      }));
    } catch (err) {
      error.value = 'Failed to fetch users';
      console.error('Error fetching users:', err);
      notify({ message: 'Failed to fetch users', type: 'ERROR' });
    } finally {
      loading.value = false;
    }
  };

  const updateUserRoles = async (userId: string, newRoles: string[]) => {
    updatingUsers.value[userId] = true;
    error.value = null;
    try {
      const token = await getToken();
      await api.patch(`/users/${userId}/roles`, { roles: newRoles }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the local state
      const userIndex = users.value.findIndex(u => u.user_id === userId);
      if (userIndex !== -1) {
        users.value[userIndex] = {
          ...users.value[userIndex],
          roles: newRoles
        };
      }
      
      notify({ message: 'User roles updated successfully', type: 'SUCCESS' });
    } catch (err) {
      error.value = 'Failed to update user roles';
      console.error('Error updating user roles:', err);
      notify({ message: 'Failed to update user roles', type: 'ERROR' });
      
      // Revert the local state by fetching the current user's roles
      try {
        const token = await getToken();
        const response = await api.get(`/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userIndex = users.value.findIndex(u => u.user_id === userId);
        if (userIndex !== -1 && response) {
          users.value[userIndex] = {
            ...users.value[userIndex],
            roles: Array.isArray(response.roles) ? response.roles : []
          };
        }
      } catch (fetchErr) {
        console.error('Error fetching updated user roles:', fetchErr);
      }
    } finally {
      updatingUsers.value[userId] = false;
    }
  };

  const initialize = async () => {
    if (initialized.value) return;
    await Promise.all([fetchUsers(), fetchAvailableRoles()]);
    initialized.value = true;
  };

  // Initialize data
  onMounted(() => {
    initialize();
  });

  return {
    users: filteredUsers,
    loading,
    error,
    availableRoles,
    updatingUsers,
    searchQuery,
    selectedRoles,
    fetchUsers,
    updateUserRoles
  };
} 